import axios from 'axios';
import { getCpapApiEndpoint, setAxiosDefaultHeaders } from './apiBaseUtilities';
import { FirebaseUserProps } from './entityUtilities';
import { debugLog } from './debugUtilities';

/**
 * Enrollment claims: cribsy-api docs/ENROLLMENT.md. The parent redeems a code SAM issued at consent; cribsy-api binds
 * the child (subject) to the study ID. Nothing here ever receives the code from the server; the parent types it or
 * opens the letter's link.
 */
const cpapApiEndpoint = getCpapApiEndpoint();

export type MyEnrollment = { studyid: string; subjectId: string; firstName: string | null; redeemedAt: string };
export type ClaimPreview = {
  studyid: string; child: { firstName: string; birthMonth?: string };
  alreadyClaimedByYou: boolean; claimedByAnother: boolean; expiresAt?: string;
};
export type ClaimCandidate = { subjectId: string; name: string | null; dob: string | null };
export type ClaimResult = {
  studyid: string; child: { firstName: string }; subjectId: string;
  subjectCreated?: boolean; dobMismatch?: boolean; assessmentsLinked?: number; contactMatch?: string; alreadyClaimed?: boolean;
};
export type StudyErrorCode = 'badcode' | 'notfound' | 'conflict' | 'candidates' | 'gone' | 'locked' | 'noaccount' | 'network' | 'other';

export class StudyApiError extends Error {
  status: number;
  code: StudyErrorCode;
  candidates?: ClaimCandidate[];
  constructor(code: StudyErrorCode, status: number, message: string, candidates?: ClaimCandidate[]) {
    super(message);
    this.name = 'StudyApiError';
    this.code = code;
    this.status = status;
    this.candidates = candidates;
  }
}

function toStudyError(err: any): StudyApiError {
  const status: number = err?.response?.status ?? 0;
  const data = err?.response?.data;
  const message: string = (typeof data?.message === 'string' ? data.message : Array.isArray(data?.message) ? data.message.join('; ') : err?.message) ?? '';
  if (status === 0) return new StudyApiError('network', 0, message);
  if (status === 400) return new StudyApiError(/user record/i.test(message) ? 'noaccount' : 'badcode', status, message);
  if (status === 404) return new StudyApiError('notfound', status, message);
  if (status === 409) return Array.isArray(data?.candidates)
    ? new StudyApiError('candidates', status, message, data.candidates)
    : new StudyApiError('conflict', status, message);
  if (status === 410) return new StudyApiError('gone', status, message);
  if (status === 429) return new StudyApiError('locked', status, message);
  return new StudyApiError('other', status, message);
}

function authorize(firebaseUser: FirebaseUserProps): boolean {
  if (!cpapApiEndpoint || !firebaseUser?.bearerToken) return false;
  setAxiosDefaultHeaders(firebaseUser.uid, firebaseUser.bearerToken);
  return true;
}

/** Codes are `<STUDYID>-<SECRET>`; accept however the parent typed it. */
export function normalizeClaimCode(raw: string | undefined | null): string {
  return (raw ?? '').toUpperCase().replace(/[^A-Z0-9-]/g, '');
}

export function looksLikeClaimCode(code: string): boolean {
  return /^[A-Z0-9]{3,32}-[A-Z0-9]{6,}$/.test(code);
}

/** The code inside a letter link: https://app.cribsy.ai/claim/CR6015-7K3MPX, /#/claim/..., or a custom scheme. */
export function parseClaimCodeFromUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const m = url.match(/\/claim\/([A-Za-z0-9-]+)/);
  if (!m) return undefined;
  const code = normalizeClaimCode(m[1]);
  return looksLikeClaimCode(code) ? code : undefined;
}

/** GET /enrollments/mine: the children this account has linked. Empty on any failure. */
export async function getMyEnrollments(firebaseUser: FirebaseUserProps | undefined): Promise<MyEnrollment[]> {
  if (!firebaseUser || !authorize(firebaseUser)) return [];
  try {
    const res = await axios.get(`${cpapApiEndpoint}enrollments/mine`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    debugLog('[study] enrollments/mine failed:', err);
    return [];
  }
}

/** POST /enrollments/hint: is a claim waiting for any contact this account carries? The server already knows the account's email and phone. */
export async function getEnrollmentHint(firebaseUser: FirebaseUserProps | undefined, extra: { email?: string; phone?: string } = {}): Promise<boolean> {
  if (!firebaseUser || !authorize(firebaseUser)) return false;
  try {
    const res = await axios.post(`${cpapApiEndpoint}enrollments/hint`, extra);
    return res.data?.pending === true;
  } catch (err) {
    debugLog('[study] enrollments/hint failed:', err);
    return false;
  }
}

/** POST /enrollments/resend: SAM resends the letter to the contact it has on file. */
export async function requestEnrollmentResend(firebaseUser: FirebaseUserProps | undefined): Promise<boolean> {
  if (!firebaseUser || !authorize(firebaseUser)) return false;
  try {
    const res = await axios.post(`${cpapApiEndpoint}enrollments/resend`, {});
    return res.data?.requested === true;
  } catch (err) {
    debugLog('[study] enrollments/resend failed:', err);
    return false;
  }
}

/** POST /enrollments/preview: what the code would link, binding nothing. Throws StudyApiError. */
export async function previewClaim(firebaseUser: FirebaseUserProps | undefined, code: string): Promise<ClaimPreview> {
  if (!firebaseUser || !authorize(firebaseUser)) throw new StudyApiError('network', 0, 'not signed in');
  try {
    const res = await axios.post(`${cpapApiEndpoint}enrollments/preview`, { code: normalizeClaimCode(code) });
    return res.data;
  } catch (err) {
    throw toStudyError(err);
  }
}

/** POST /enrollments/claim: bind. `subjectId` only when the server asked the parent to choose (409 candidates). Throws StudyApiError. */
export async function redeemClaim(firebaseUser: FirebaseUserProps | undefined, code: string, subjectId?: string): Promise<ClaimResult> {
  if (!firebaseUser || !authorize(firebaseUser)) throw new StudyApiError('network', 0, 'not signed in');
  try {
    const body: any = { code: normalizeClaimCode(code) };
    if (subjectId) body.subjectId = subjectId;
    const res = await axios.post(`${cpapApiEndpoint}enrollments/claim`, body);
    return res.data;
  } catch (err) {
    throw toStudyError(err);
  }
}
